import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { Sparkles, BookOpen, Loader2, ChevronRight, Star, Target, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function RecommendationsPage() {
  const { api, user } = useAuth();
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingLearners, setFetchingLearners] = useState(true);

  useEffect(() => {
    fetchLearners();
  }, []);

  const fetchLearners = async () => {
    try {
      const response = await api.get('/learners/');
      const data = response.data.results || response.data;
      setLearners(data);
      if (data.length > 0) {
        setSelectedLearner(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch learners:', error);
      toast.error('Failed to load learners');
    } finally {
      setFetchingLearners(false);
    }
  };

  const getRecommendations = async (learnerId) => {
    setLoading(true);
    setRecommendations(null);
    try {
      const response = await api.get(`/ai/recommendations/${learnerId}`);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      toast.error(error.response?.data?.error || 'Failed to get recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleLearnerSelect = (learner) => {
    setSelectedLearner(learner);
    setRecommendations(null);
  };

  const confidenceColor = {
    high: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    low: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
  };

  if (fetchingLearners) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (learners.length === 0) {
    return (
      <div className="space-y-6" data-testid="recommendations-page">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          Course Recommendations
        </h1>
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Learners Found</h2>
            <p className="text-muted-foreground mb-4">Add a learner to get personalized course recommendations.</p>
            <Link to="/parent/learners">
              <Button>Add Learner</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          AI Course Recommendations
        </h1>
        <p className="text-muted-foreground">Get personalized course suggestions powered by AI</p>
      </div>

      {/* Learner Selection */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Select Learner</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {learners.map((learner) => (
              <Button
                key={learner.id}
                variant={selectedLearner?.id === learner.id ? 'default' : 'outline'}
                onClick={() => handleLearnerSelect(learner)}
                data-testid={`learner-btn-${learner.id}`}
              >
                {learner.first_name} {learner.last_name}
                {learner.grade && <span className="ml-2 text-xs opacity-70">Grade {learner.grade}</span>}
              </Button>
            ))}
          </div>
          
          {selectedLearner && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedLearner.first_name} {selectedLearner.last_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedLearner.school || 'School not specified'} 
                    {selectedLearner.grade && ` • Grade ${selectedLearner.grade}`}
                  </p>
                </div>
                <Button 
                  onClick={() => getRecommendations(selectedLearner.id)}
                  disabled={loading}
                  data-testid="get-recommendations-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get Recommendations
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recommendations Results */}
      {recommendations && (
        <div className="space-y-6">
          {/* Learning Path Advice */}
          {recommendations.learning_path_advice && (
            <Card className="card-dashboard border-primary/20 bg-primary/5">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-primary">AI Learning Path Advice</p>
                    <p className="text-sm mt-1">{recommendations.learning_path_advice}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="card-dashboard">
              <CardContent className="py-4 text-center">
                <BookOpen className="h-8 w-8 mx-auto text-primary mb-2" />
                <p className="text-2xl font-bold">{recommendations.completed_courses_count}</p>
                <p className="text-sm text-muted-foreground">Courses Completed</p>
              </CardContent>
            </Card>
            <Card className="card-dashboard">
              <CardContent className="py-4 text-center">
                <Star className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                <p className="text-2xl font-bold">{recommendations.badges_earned}</p>
                <p className="text-sm text-muted-foreground">Badges Earned</p>
              </CardContent>
            </Card>
            <Card className="card-dashboard">
              <CardContent className="py-4 text-center">
                <Target className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold">{recommendations.learner?.points_balance || 0}</p>
                <p className="text-sm text-muted-foreground">Points Balance</p>
              </CardContent>
            </Card>
          </div>

          {/* Recommended Courses */}
          {recommendations.recommendations?.length > 0 ? (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold">Recommended Next Courses</h2>
              {recommendations.recommendations.map((rec, index) => (
                <Card key={index} className="card-dashboard hover:border-primary/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{rec.title}</h3>
                          <Badge className={confidenceColor[rec.confidence] || confidenceColor.medium}>
                            {rec.confidence} match
                          </Badge>
                        </div>
                        <p className="text-muted-foreground">{rec.reason}</p>
                      </div>
                      <Link to="/catalog">
                        <Button variant="outline" size="sm">
                          View Course
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : recommendations.message ? (
            <Card className="card-dashboard">
              <CardContent className="py-8 text-center">
                <Star className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Amazing Achievement!</h3>
                <p className="text-muted-foreground">{recommendations.message}</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
